import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFieldDecimalPlacesConfig } from 'hooks';
import { useTranslation } from 'react-i18next';
import { StyledTableBodyCell, StyledTableRow } from 'components/common/Styles';
import { IconButton, TextField } from '@totalsoft/rocket-ui';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isValid, getErrors, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { insuranceUsageGridValidator } from 'features/insuranceConventions/edit/validators';
import { isValidNumber } from 'utils/functions';
import { ADMIN_INSURANCE_CONVENTIONS_WRITE } from 'constants/rightsCodes';
import { usePermission } from 'providers/RightsHooks';
import FlexBox from 'components/common/FlexBox';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';

const InsuranceConventionUsageGridComponent = ({ usageGrid, onMutate, onUpdateLocal, onDelete, onCancelAdd }) => {
  const { cvsChPercent } = useFieldDecimalPlacesConfig();
  const [validation, validate, resetValidation] = useDirtyFieldValidation(insuranceUsageGridValidator);
  const editing = useMemo(() => usageGrid.editing || usageGrid.isNew, [usageGrid.editing, usageGrid.isNew]);
  const disabled = useMemo(() => usageGrid.disabled, [usageGrid.disabled]);
  const canWrite = usePermission(ADMIN_INSURANCE_CONVENTIONS_WRITE);

  const updateLocal = useCallback((property, value) => onUpdateLocal(usageGrid.id, property, value), [onUpdateLocal, usageGrid]);

  const handleMutateUsageGrid = useCallback(
    usageGrid => {
      if (!validate(usageGrid)) return;
      onMutate(usageGrid);
    },
    [validate, onMutate]
  );

  const handleEdit = useCallback(() => updateLocal('editing', true), [updateLocal]);

  const handleCancelEdit = useCallback(() => {
    if (usageGrid.isNew) onCancelAdd();
    else {
      updateLocal('editing', false);
      resetValidation();
    }
  }, [usageGrid, onCancelAdd, updateLocal, resetValidation]);

  const handleDeleteUsageGrid = useCallback(() => {
    onDelete(usageGrid.id);
  }, [usageGrid, onDelete]);

  return (
    <StyledTableRow>
      {editing ? (
        <UsageGridEditView
          usageGrid={usageGrid}
          validation={validation}
          disabled={disabled || !canWrite}
          formatPercent={cvsChPercent}
          onMutate={handleMutateUsageGrid}
          onDelete={handleDeleteUsageGrid}
          onCancelEdit={handleCancelEdit}
        />
      ) : (
        <UsageGridDetailView
          usageGrid={usageGrid}
          disabled={disabled || !canWrite}
          formatPercent={cvsChPercent}
          onEdit={handleEdit}
          onDelete={handleDeleteUsageGrid}
        />
      )}
    </StyledTableRow>
  );
};

InsuranceConventionUsageGridComponent.propTypes = {
  usageGrid: PropTypes.object,
  validation: PropTypes.object,
  onMutate: PropTypes.func,
  onUpdateLocal: PropTypes.func,
  onDelete: PropTypes.func,
  onCancelAdd: PropTypes.func
};

const UsageGridDetailView = ({ usageGrid, disabled, formatPercent, onEdit, onDelete }) => {
  const { t } = useTranslation();

  const yearLabel = useMemo(
    () => t(`InsuranceConventions.Details.UsageTable.${usageGrid.age === 1 ? 'Year' : 'Years'}`),
    [usageGrid.age, t]
  );

  return (
    <>
      <StyledTableBodyCell>
        {usageGrid.age} {yearLabel.toLowerCase()}
      </StyledTableBodyCell>
      <StyledTableBodyCell>{usageGrid.usagePercent?.toFixed(formatPercent)}</StyledTableBodyCell>
      <StyledTableBodyCell>{usageGrid.insurancePercent?.toFixed(formatPercent)}</StyledTableBodyCell>
      <StyledTableBodyCell>
        <FlexBox gap={0.5} justifyContent='end'>
          <IconButton type='edit' color='primary' size='tiny' variant='text' onClick={onEdit} tooltip={t('Edit')} disabled={disabled} />
          <IconButton
            type='delete'
            color='primary'
            size='tiny'
            variant='text'
            onClick={onDelete}
            tooltip={t('Delete')}
            disabled={disabled}
          />
        </FlexBox>
      </StyledTableBodyCell>
    </>
  );
};

UsageGridDetailView.propTypes = {
  usageGrid: PropTypes.object,
  disabled: PropTypes.bool,
  formatPercent: PropTypes.number,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func
};

const UsageGridEditView = ({ usageGrid, validation, disabled, formatPercent, onMutate, onDelete, onCancelEdit }) => {
  const { t } = useTranslation();

  const [lens, dirtyInfo] = useChangeTrackingLens(usageGrid);
  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useGlobalIsDirty(isDirty);

  return (
    <>
      <StyledTableBodyCell>
        <TextField
          label={t('InsuranceConventions.Details.UsageTable.Age')}
          isNumeric
          fullWidth
          endAdornment={t('InsuranceConventions.Details.UsageTable.Years').toLowerCase()}
          decimalScale={0}
          value={isValidNumber(lens.age |> get) ? lens.age |> get : ''}
          onChange={lens.age |> set}
          disabled={disabled}
          error={!isValid(validation.age)}
          helperText={getErrors(validation.age)}
        />
      </StyledTableBodyCell>
      <StyledTableBodyCell>
        <TextField
          label={t('InsuranceConventions.Details.UsageTable.UsagePercent')}
          isNumeric
          fullWidth
          value={(lens.usagePercent |> get) || ''}
          onChange={lens.usagePercent |> set}
          disabled={disabled}
          decimalScale={formatPercent}
          error={!isValid(validation.usagePercent)}
          helperText={getErrors(validation.usagePercent)}
        />
      </StyledTableBodyCell>
      <StyledTableBodyCell align='center'>
        <TextField
          label={t('InsuranceConventions.Details.UsageTable.InsurancePercent')}
          isNumeric
          fullWidth
          value={(lens.insurancePercent |> get) || ''}
          onChange={lens.insurancePercent |> set}
          disabled={disabled}
          decimalScale={formatPercent}
          error={!isValid(validation.insurancePercent)}
          helperText={getErrors(validation.insurancePercent)}
        />
      </StyledTableBodyCell>
      <StyledTableBodyCell>
        <FlexBox gap={0.5} justifyContent='end'>
          <IconButton
            type='save'
            color='primary'
            size='tiny'
            variant='text'
            onClick={() => {
              onMutate(lens |> get);
            }}
            tooltip={t('Save')}
            disabled={disabled || !isDirty}
          />
          <IconButton
            type='cancel'
            color='primary'
            size='tiny'
            variant='text'
            onClick={onCancelEdit}
            tooltip={t('Cancel')}
            disabled={disabled}
          />
          {!usageGrid.isNew && (
            <IconButton
              type='delete'
              color='primary'
              size='tiny'
              variant='text'
              onClick={onDelete}
              tooltip={t('Delete')}
              disabled={disabled}
            />
          )}
        </FlexBox>
      </StyledTableBodyCell>
    </>
  );
};

UsageGridEditView.propTypes = {
  usageGrid: PropTypes.object,
  validation: PropTypes.object,
  disabled: PropTypes.bool,
  formatPercent: PropTypes.number,
  onMutate: PropTypes.func,
  onDelete: PropTypes.func,
  onCancelEdit: PropTypes.func
};

export default InsuranceConventionUsageGridComponent;
